<!-- src/components/SidebarMenu.vue - MOBILNA VERZIJA OPTIMIZOVANA -->
<template>
  <div
    class="sidebar-container h-screen fixed left-0 top-0 z-20 transition-all duration-300"
    :class="{
      'w-64': isOpen && isAuthenticated && !isMobile,
      'w-16': !isOpen || !isAuthenticated || isMobile,
    }"
  >
    <!-- Blur overlay for non-authenticated users -->
    <div
      v-if="!isAuthenticated"
      class="absolute inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm z-10 flex flex-col items-center justify-center px-0"
    >
      <div class="text-center p-1">
        <p class="text-white text-xs mb-2">Prijavi se</p>
        <router-link
          to="/login"
          class="block bg-indigo-600 text-white rounded-md hover:bg-indigo-500 transition-colors text-center w-full py-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 mx-auto"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M3 3a1 1 0 011-1h12a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm2 0v14h12V3H5z"
              clip-rule="evenodd"
            />
            <path
              d="M11 6a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6z"
            />
          </svg>
        </router-link>
      </div>
    </div>

    <!-- Sidebar content -->
    <div
      class="h-full bg-gray-800 text-white flex flex-col shadow-lg relative sidebar-content"
      :class="{ 'blur-sm': !isAuthenticated }"
    >
      <!-- Toggle button - SAKRIVEN NA MOBILNIM UREĐAJIMA -->
      <button
        @click="toggleSidebar"
        class="absolute -right-3 bg-gray-700 rounded-full p-1 text-white shadow-md toggle-button hidden md:block"
        :class="{ hidden: !isAuthenticated }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          :class="{ 'rotate-180': !isOpen }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
      </button>

      <!-- Logo or brand -->
      <div class="px-4 mb-4 flex justify-center sidebar-logo">
        <router-link to="/" class="block hover:opacity-80 transition-opacity">
          <img src="@/assets/logo.png" alt="Logo" class="h-12 sm:h-12" />
        </router-link>
      </div>

      <!-- Navigation links -->
      <nav
        class="flex-1 px-2 space-y-1 overflow-y-auto scrollbar-hide sidebar-nav"
        style="scrollbar-width: none; -ms-overflow-style: none"
      >
        <!-- Create new listing link -->
        <router-link
          to="/create-product"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="sidebar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            <span v-if="isOpen && !isMobile" class="sidebar-text"
              >Dodaj novi oglas</span
            >
            <div v-if="!isOpen || isMobile" class="tooltip">
              Dodaj novi oglas
            </div>
          </div>
        </router-link>

        <router-link
          to="/my-listings"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="sidebar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"
              />
            </svg>
            <span v-if="isOpen && !isMobile" class="sidebar-text"
              >Moji oglasi</span
            >
            <div v-if="!isOpen || isMobile" class="tooltip">Moji oglasi</div>
          </div>
        </router-link>

        <!-- Messages with badge -->
        <router-link
          to="/messages"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <div class="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="sidebar-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"
                />
                <path
                  d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"
                />
              </svg>
              <!-- Message badge -->
              <span
                v-if="hasUnreadMessages"
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium"
                :class="{ 'animate-pulse': hasNewNotification }"
              >
                {{ badges.messages > 9 ? '9+' : badges.messages }}
              </span>
            </div>
            <span v-if="isOpen && !isMobile" class="sidebar-text">Poruke</span>
            <div v-if="!isOpen || isMobile" class="tooltip">
              Poruke
              <span v-if="hasUnreadMessages" class="ml-1 text-red-400"
                >({{ badges.messages }})</span
              >
            </div>
          </div>
        </router-link>

        <!-- Notifications with badge and dropdown -->
        <div class="relative">
          <button
            @click="toggleNotifications"
            class="sidebar-link group w-full text-left relative"
            :class="{ 'sidebar-link-active bg-gray-900': showNotifications }"
          >
            <div class="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="sidebar-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                />
              </svg>
              <!-- Total notification badge -->
              <span
                v-if="totalBadgeCount > 0"
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium"
                :class="{ 'animate-pulse': hasNewNotification }"
              >
                {{ totalBadgeCount > 9 ? '9+' : totalBadgeCount }}
              </span>

              <!-- Connection status indicator -->
              <span
                class="absolute -bottom-1 -right-1 w-2 h-2 rounded-full border border-gray-800"
                :class="{
                  'bg-green-500': isConnected,
                  'bg-red-500': !isConnected && connectionError,
                  'bg-yellow-500': !isConnected && !connectionError,
                }"
              />
            </div>
            <span v-if="isOpen && !isMobile" class="sidebar-text"
              >Obaveštenja</span
            >
            <div v-if="!isOpen || isMobile" class="tooltip">
              Obaveštenja
              <span v-if="totalBadgeCount > 0" class="ml-1 text-red-400"
                >({{ totalBadgeCount }})</span
              >
            </div>
          </button>

          <!-- Notifications Dropdown -->
          <NotificationDropdown
            :show="showNotifications && isAuthenticated"
            :is-sidebar-open="isOpen && !isMobile"
            @close="showNotifications = false"
          />
        </div>

        <router-link
          to="/ratings"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="sidebar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
            <span v-if="isOpen && !isMobile" class="sidebar-text">Ocene</span>
            <div v-if="!isOpen || isMobile" class="tooltip">Ocene</div>
          </div>
        </router-link>

        <router-link
          to="/following"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="sidebar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                clip-rule="evenodd"
              />
            </svg>
            <span v-if="isOpen && !isMobile" class="sidebar-text">Pratim</span>
            <div v-if="!isOpen || isMobile" class="tooltip">Pratim</div>
          </div>
        </router-link>

        <router-link
          to="/account"
          class="sidebar-link group relative"
          active-class="sidebar-link-active"
          v-slot="{ isActive }"
        >
          <div :class="[isActive ? 'sidebar-link-active' : 'sidebar-link']">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="sidebar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clip-rule="evenodd"
              />
            </svg>
            <span v-if="isOpen && !isMobile" class="sidebar-text"
              >Moj nalog</span
            >
            <div v-if="!isOpen || isMobile" class="tooltip">Moj nalog</div>
          </div>
        </router-link>

        <button
          @click="logout"
          class="sidebar-link group w-full text-left relative"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="sidebar-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414l-4-4H3zm7 2a1 1 0 011 1v6.414l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L12 12.414V6a1 1 0 011-1h2a1 1 0 110 2h-1v.586l1.707 1.707A1 1 0 0016 10v5a3 3 0 01-3 3H5a3 3 0 01-3-3V5a3 3 0 013-3h5a1 1 0 010 2H5v-.001z"
              clip-rule="evenodd"
            />
          </svg>
          <span v-if="isOpen && !isMobile" class="sidebar-text"
            >Izloguj se</span
          >
          <div v-if="!isOpen || isMobile" class="tooltip">Izloguj se</div>
        </button>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications';
import NotificationDropdown from '@/components/NotificationDropdown.vue';

export default defineComponent({
  name: 'SidebarMenu',
  components: {
    NotificationDropdown,
  },
  emits: ['update:sidebarState'],
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const isOpen = ref(true);
    const showNotifications = ref(false);
    const hasNewNotification = ref(false);
    const isMobile = ref(false);

    // Use notifications composable
    const {
      badges,
      totalBadgeCount,
      isConnected,
      connectionError,
      hasUnreadMessages,
      initializeNotifications,
      cleanupNotifications,
    } = useNotifications();

    // Check if device is mobile
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    // Debug: Log kad god se badge promeni
    watch(
      badges,
      (newBadges) => {
        console.log('[SidebarMenu] Badges changed:', newBadges);
      },
      { deep: true }
    );

    watch(totalBadgeCount, (newCount) => {
      console.log('[SidebarMenu] Total badge count changed:', newCount);
    });

    // Track new notifications for animation
    let previousBadgeCount = 0;

    watch(totalBadgeCount, (newCount) => {
      if (newCount > previousBadgeCount) {
        hasNewNotification.value = true;
        setTimeout(() => {
          hasNewNotification.value = false;
        }, 3000);
      }
      previousBadgeCount = newCount;
    });

    const updateSidebarState = (isOpenState: boolean) => {
      if (!store.state.auth.isLoggedIn) {
        isOpenState = false;
      }

      // NOVA LOGIKA: Na mobilnim uređajima uvek drži sidebar zatvorenim
      if (isMobile.value) {
        isOpenState = false;
      }

      isOpen.value = isOpenState;
      localStorage.setItem('sidebarOpen', isOpenState.toString());

      document.dispatchEvent(
        new CustomEvent('sidebarStateChanged', {
          detail: { isOpen: isOpenState },
        })
      );
    };

    const isAuthenticated = computed(() => {
      return store.state.auth.isLoggedIn;
    });

    const handleCloseNotificationDropdown = () => {
      showNotifications.value = false;
    };

    // Toggle notifications dropdown
    const toggleNotifications = () => {
      console.log('[SidebarMenu] Toggling notifications dropdown');
      showNotifications.value = !showNotifications.value;
      console.log(
        '[SidebarMenu] Notifications dropdown now:',
        showNotifications.value
      );
    };

    // Close notifications when clicking outside - samo za Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showNotifications.value) {
        showNotifications.value = false;
      }
    };

    watch(isAuthenticated, (newValue, oldValue) => {
      if (newValue === true && oldValue === false) {
        updateSidebarState(true);
        initializeNotifications();
      } else if (!newValue) {
        updateSidebarState(false);
        showNotifications.value = false;
        cleanupNotifications();
      }
    });

    const handleResize = () => {
      checkMobile();
      if (isMobile.value && isAuthenticated.value) {
        updateSidebarState(false);
      }
    };

    const mql = window.matchMedia('(max-width: 768px)');

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      checkMobile();
      if (e.matches && isAuthenticated.value) {
        updateSidebarState(false);
      }
    };

    onMounted(() => {
      checkMobile(); // Initial check

      window.addEventListener('resize', handleResize);
      window.addEventListener('keydown', handleKeyDown);
      mql.addEventListener('change', handleMediaChange);

      document.addEventListener(
        'closeNotificationDropdown',
        handleCloseNotificationDropdown
      );

      if (isAuthenticated.value) {
        handleMediaChange(mql);
        const savedState = localStorage.getItem('sidebarOpen');
        if (savedState !== null && !isMobile.value) {
          isOpen.value = savedState === 'true';
          document.dispatchEvent(
            new CustomEvent('sidebarStateChanged', {
              detail: { isOpen: isOpen.value },
            })
          );
        }
        initializeNotifications();
      } else {
        updateSidebarState(false);
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      mql.removeEventListener('change', handleMediaChange);
      document.removeEventListener(
        'closeNotificationDropdown',
        handleCloseNotificationDropdown
      );
    });

    const toggleSidebar = () => {
      // NOVA LOGIKA: Blokiraj toggle na mobilnim uređajima
      if (isMobile.value) {
        return;
      }
      updateSidebarState(!isOpen.value);
    };

    const logout = () => {
      cleanupNotifications();
      store.dispatch('auth/logout');
      updateSidebarState(false);
      showNotifications.value = false;
      router.push('/login');
    };

    return {
      isOpen,
      isAuthenticated,
      showNotifications,
      hasNewNotification,
      isMobile,
      badges,
      totalBadgeCount,
      isConnected,
      connectionError,
      hasUnreadMessages,
      toggleSidebar,
      toggleNotifications,
      logout,
    };
  },
});
</script>

<style scoped>
.sidebar-container {
  @apply h-screen fixed left-0 top-0 z-20;
  transition: width 0.3s ease;
  /* Use viewport height to account for mobile browsers */
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for modern browsers */
}

/* Sidebar content responsive padding */
.sidebar-content {
  /* Desktop: Normal spacing */
  @apply pt-20 pb-6;
}

/* Mobile adjustments */
@media (max-width: 767px) {
  .sidebar-content {
    /* Mobile: More padding to avoid header/footer overlap */
    padding-top: 6rem !important; /* 96px */
    padding-bottom: 5rem !important; /* 80px */
  }
}

/* Very small screens */
@media (max-width: 480px) {
  .sidebar-content {
    padding-top: 7rem !important; /* 112px */
    padding-bottom: 6rem !important; /* 96px */
  }
}

/* Short screens (landscape mobile) */
@media (max-height: 600px) {
  .sidebar-content {
    padding-top: 4rem !important; /* 64px */
    padding-bottom: 3rem !important; /* 48px */
  }

  .sidebar-logo {
    margin-bottom: 0.5rem !important;
  }

  .sidebar-nav {
    padding-bottom: 0.5rem !important;
  }
}

/* Extra short screens */
@media (max-height: 500px) {
  .sidebar-content {
    padding-top: 3rem !important; /* 48px */
    padding-bottom: 2rem !important; /* 32px */
  }

  .sidebar-logo {
    display: none; /* Hide logo on very short screens */
  }
}

/* Toggle button positioning */
.toggle-button {
  top: 6rem; /* Default position */
}

@media (max-width: 767px) {
  .toggle-button {
    top: 7rem; /* Mobile position */
  }
}

@media (max-height: 600px) {
  .toggle-button {
    top: 4.5rem; /* Short screen position */
  }
}

@media (max-height: 500px) {
  .toggle-button {
    top: 3.5rem; /* Very short screen position */
  }
}

.sidebar-link {
  @apply flex items-center px-2 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors;
}

.sidebar-link-active {
  @apply flex items-center px-2 py-3 text-sm font-medium rounded-md bg-gray-900 text-white;
}

.sidebar-icon {
  @apply mr-3 flex-shrink-0 h-6 w-6;
}

.sidebar-text {
  @apply truncate;
}

.tooltip {
  @apply invisible absolute left-12 top-0 mt-2 w-auto px-3 py-1 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50;
  transition: visibility 0s linear 0.1s, opacity 0.1s linear;
  opacity: 0;
  white-space: nowrap;
}

.sidebar-link:hover .tooltip {
  @apply visible;
  transition-delay: 0.3s;
  opacity: 1;
}

.tooltip:before {
  content: '';
  @apply absolute top-2 -left-1 w-2 h-2 bg-gray-900 transform rotate-45;
}

/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}
</style>
